import React from 'react';
import { BarChart3 } from 'lucide-react';
import { DeploymentRequest } from '../../types';
import { computeStatusStrip } from '../../utils/deploymentAnalytics';
import { STATUS_COLORS } from '../../utils/chartColors';

interface DeploymentStatusStripProps {
  deployments: DeploymentRequest[];
  limit?: number;
}

const NEUTRAL_COLOR = '#c3c2b7';

const DeploymentStatusStrip: React.FC<DeploymentStatusStripProps> = ({ deployments, limit = 90 }) => {
  const entries = computeStatusStrip(deployments, limit);

  const colorFor = (status: string) =>
    status === 'success' ? STATUS_COLORS.good : status === 'fail' ? STATUS_COLORS.critical : NEUTRAL_COLOR;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-emerald-100 text-emerald-600">
            <BarChart3 className="w-4 h-4" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Deployment Status</h3>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS.good }} /> Deployed
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS.critical }} /> Rejected
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: NEUTRAL_COLOR }} /> In progress
          </span>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-4">Last {entries.length} deployment requests, oldest to newest</p>

      {entries.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">No deployment history yet</div>
      ) : (
        <div className="flex items-end gap-[3px] h-16">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="flex-1 rounded-sm min-w-[3px] h-full hover:opacity-70 transition-opacity cursor-default"
              style={{ backgroundColor: colorFor(entry.status) }}
              title={`${entry.title} — ${entry.date}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DeploymentStatusStrip;
